import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { compactMoney, money } from "./format";

const LABELS = {
  bud: "Budget (plan)",
  act: "Actual",
  inv: "Invoiced",
  pd: "Paid",
};

/**
 * Cumulative cost by month - the body of the S-curve accordion panel.
 * Budget is the plan; Actual, Invoiced and Paid stop at their last dated
 * amount. The dashed line is the Estimate total, the ceiling the others are
 * measured against.
 *
 * @param curve          buildSCurve() result
 * @param estimateTotal  overall Estimate, or null
 */
export default function DashboardSCurve({ curve, estimateTotal }) {
  const { palette } = useTheme();
  const colors = {
    bud: palette.secondary.main,
    act: palette.warning.main,
    inv: palette.primary.main,
    pd: palette.success.main,
    est: palette.text.secondary,
  };

  const undated = Object.entries(curve.undated || {}).filter(
    ([, value]) => value > 0,
  );

  const series = curve.measures.map((key) => ({
    id: key,
    label: LABELS[key],
    color: colors[key],
    data: curve.series[key],
    curve: "monotoneX",
    showMark: true,
    valueFormatter: (value) => (value == null ? "" : money(value)),
  }));

  if (series.length > 0 && estimateTotal > 0) {
    series.push({
      id: "est",
      label: "Estimate total",
      color: colors.est,
      data: curve.labels.map(() => estimateTotal),
      showMark: false,
      valueFormatter: (value) => money(value),
    });
  }

  return (
    <Box>
      {series.length === 0 ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No dated amounts yet. Use “Enter data” to add budget, actual,
            invoiced or paid entries — or link POs, payments and MIS invoices.
          </Typography>
        </Box>
      ) : (
        <LineChart
          height={340}
          margin={{ top: 16, right: 24, bottom: 70, left: 64 }}
          xAxis={[{ scaleType: "point", data: curve.labels }]}
          yAxis={[{ valueFormatter: compactMoney }]}
          series={series}
          slotProps={{
            legend: {
              direction: "row",
              position: { vertical: "bottom", horizontal: "middle" },
              padding: 0,
            },
          }}
          sx={{
            "& .MuiLineElement-series-est": { strokeDasharray: "6 4" },
          }}
        />
      )}

      {undated.length > 0 && (
        <Typography variant="caption" color="warning.main" component="div">
          {undated
            .map(([key, value]) => `${money(value)} of ${LABELS[key]}`)
            .join(", ")}{" "}
          has no usable date and isn’t on the curve.
        </Typography>
      )}
    </Box>
  );
}
